import { useCallback, useMemo } from 'react';

import {
  getChainId,
  useGetChainIdQuery,
} from 'modules/auth/actions/getChainId';
import {
  getCurrentAccount,
  useGetCurrentAccountQuery,
} from 'modules/auth/actions/getCurrentAccount';
import { useAppDispatch } from 'modules/store/useAppDispatch';

import { useConnectMutation } from '../actions/connect';
import { useDisconnectMutation } from '../actions/disconnect';
import { CONNECTION_CACHE_KEY } from '../const';

const UNAUTHORIZED_CONNECTION_KEY = '__unauthorized_connection_key';

export function useConnection() {
  const [connect, { data, isLoading: isConnectLoading }] = useConnectMutation({
    fixedCacheKey: CONNECTION_CACHE_KEY,
  });

  const { data: currentAccount } = useGetCurrentAccountQuery(undefined);
  const isConnected = !!currentAccount;

  const { data: chainId } = useGetChainIdQuery();
  const dispatch = useAppDispatch();

  const [disconnect, { isLoading: isDisconnectLoading }] =
    useDisconnectMutation({
      fixedCacheKey: CONNECTION_CACHE_KEY,
    });

  const handleConnect = useCallback(
    async (walletId: string) => {
      await connect(walletId);

      void dispatch(getChainId.initiate());
      void dispatch(getCurrentAccount.initiate());
    },
    [connect, dispatch],
  );

  const handleDisconnect = useCallback(async () => {
    if (data?.walletId) {
      await disconnect({ walletId: data.walletId });
    }
  }, [data, disconnect]);

  const actualData = useMemo(
    () => ({
      ...data,
      address: currentAccount,
      chainId: chainId ?? data?.chainId,
    }),
    [data, currentAccount, chainId],
  );

  const connectionKey = useMemo(() => {
    if (actualData?.chainId && currentAccount) {
      return `${actualData.chainId}-${currentAccount}`;
    }

    return UNAUTHORIZED_CONNECTION_KEY;
  }, [actualData.chainId, currentAccount]);

  return {
    connectionKey,
    isLoading: isConnectLoading || isDisconnectLoading,
    data: actualData,
    connect: handleConnect,
    disconnect: handleDisconnect,
    isConnected: isConnected && connectionKey !== UNAUTHORIZED_CONNECTION_KEY,
  };
}
